/** SUNTRONIX ONE — Experience settings (ONE VIBE).
 *  Personal animation / sound / density / theme preferences.  Applies instantly
 *  (localStorage) and syncs to the backend.  Never alters business/security rules.
 */
import { useEffect, useState } from "react";
import { Sparkles, Volume2, Rows3, SunMoon, Check } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { getPrefs, savePrefs, DEFAULT_PREFS } from "@/lib/experience";
import { Switch } from "@/components/ui/switch";

const OPTS = {
  animation: [
    { v: "on", label: "On", hint: "Full micro-interactions & celebrations" },
    { v: "reduced", label: "Reduced", hint: "Minimal motion, static success cards" },
    { v: "off", label: "Off", hint: "No animation" },
  ],
  density: [
    { v: "comfortable", label: "Comfortable", hint: "Roomy spacing" },
    { v: "compact", label: "Compact", hint: "More on screen" },
  ],
  theme: [
    { v: "system", label: "System" },
    { v: "light", label: "Light" },
    { v: "dark", label: "Dark" },
  ],
};

export default function ExperienceSettings() {
  const [prefs, setPrefs] = useState(getPrefs());
  const [saving, setSaving] = useState(false);
  const [savedTick, setSavedTick] = useState(false);

  useEffect(() => {
    api.get("/experience/prefs").then(({ data }) => {
      const merged = { ...DEFAULT_PREFS, ...(data.prefs || {}) };
      setPrefs(merged); savePrefs(merged);
    }).catch(() => {});
  }, []);

  const update = async (patch) => {
    const next = { ...prefs, ...patch };
    setPrefs(next); savePrefs(next);
    setSaving(true);
    try {
      await api.put("/experience/prefs", patch);
      setSavedTick(true); setTimeout(() => setSavedTick(false), 1400);
    } catch { toast.error("Could not save preference"); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-2xl mx-auto py-6 space-y-6" data-testid="experience-settings-page">
      <div>
        <div className="text-[10px] font-semibold tracking-[0.16em] uppercase text-slate-400">Profile</div>
        <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>
          Experience
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Tune how SUNTRONIX ONE looks and feels for you.
          {savedTick && <span className="ml-2 inline-flex items-center gap-1 text-emerald-600 st-saved"><Check className="h-3.5 w-3.5" /> Saved</span>}
        </p>
      </div>

      <Section icon={Sparkles} title="Animation" desc="Motion for celebrations, hovers and transitions.">
        <SegGroup name="animation" value={prefs.animation}
          options={OPTS.animation} onChange={(v) => update({ animation: v })} />
      </Section>

      <Section icon={Volume2} title="Celebration sound" desc="A subtle chime when you land a win. Off by default.">
        <label className="flex items-center gap-3 text-sm text-slate-700" data-testid="exp-sound-toggle">
          <Switch checked={!!prefs.sound} onCheckedChange={(v) => update({ sound: v })} />
          {prefs.sound ? "On" : "Off"}
        </label>
      </Section>

      <Section icon={Rows3} title="Density" desc="How much information is packed on screen.">
        <SegGroup name="density" value={prefs.density}
          options={OPTS.density} onChange={(v) => update({ density: v })} />
      </Section>

      <Section icon={SunMoon} title="Theme" desc="Dark theme is rolling out gradually across screens.">
        <SegGroup name="theme" value={prefs.theme}
          options={OPTS.theme} onChange={(v) => update({ theme: v })} />
      </Section>

      <Section icon={Sparkles} title="ONE Moments" desc="Celebrate genuine business milestones.">
        <label className="flex items-center gap-3 text-sm text-slate-700" data-testid="exp-moments-toggle">
          <Switch checked={!!prefs.moments} onCheckedChange={(v) => update({ moments: v })} />
          {prefs.moments ? "On" : "Off"}
        </label>
      </Section>

      {saving && <div className="text-[11px] text-slate-400">Saving…</div>}
    </div>
  );
}

function Section({ icon: Icon, title, desc, children }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5">
      <div className="flex items-start gap-3">
        <div className="h-9 w-9 rounded-lg bg-slate-50 border border-slate-200 grid place-items-center text-slate-500 shrink-0">
          <Icon className="h-4.5 w-4.5" strokeWidth={1.8} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-slate-900">{title}</div>
          <div className="text-[12px] text-slate-500">{desc}</div>
          <div className="mt-3">{children}</div>
        </div>
      </div>
    </div>
  );
}

function SegGroup({ name, value, options, onChange }) {
  return (
    <div className="inline-flex flex-wrap rounded-lg border border-slate-200 overflow-hidden bg-slate-50">
      {options.map((o) => {
        const active = value === o.v;
        return (
          <button key={o.v} onClick={() => onChange(o.v)} title={o.hint}
            data-testid={`exp-${name}-${o.v}`}
            className={
              "px-3.5 py-2 text-[13px] font-medium transition-colors " +
              (active ? "bg-slate-900 text-white" : "bg-transparent text-slate-600 hover:bg-white")
            }>
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
